import { useEffect, useRef, useState } from "react";
import { useClassificationContext } from "../contexts/ClassificationContext";
import { getReasonsByClassificationId } from "../services/reasonService";

/**
 * Hook that fetches and returns reasons for a given classification ID.
 * Uses the ClassificationContext to cache results and avoid duplicate API calls.
 * Route: GET /issue-reason?issue_classification_id={classificationId}
 *
 * @param classificationId - The classification ID to fetch reasons for (null/undefined = no fetch)
 * @returns [reasons, isLoading] where reasons is the filtered list for the given classification
 */
const useFetchReasonsByClassification = (
  classificationId: number | null | undefined
): [any[], boolean] => {
  const { reasonsCache, setReasonsCache } = useClassificationContext();
  const [isLoading, setIsLoading] = useState(false);
  const inFlightRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!classificationId) return;

    // Already cached — no need to fetch
    if (reasonsCache[classificationId] !== undefined) return;

    // Already fetching — skip duplicate request
    if (inFlightRef.current.has(classificationId)) return;

    inFlightRef.current.add(classificationId);
    setIsLoading(true);

    getReasonsByClassificationId(classificationId)
      .then((data) => {
        const reasons = data || [];
        setReasonsCache((prev) => ({ ...prev, [classificationId]: reasons }));
      })
      .catch((error) => {
        console.error(
          `useFetchReasonsByClassification: error fetching reasons for classification ${classificationId}`,
          error
        );
        setReasonsCache((prev) => ({ ...prev, [classificationId]: [] }));
      })
      .finally(() => {
        inFlightRef.current.delete(classificationId);
        setIsLoading(false);
      });
  }, [classificationId, reasonsCache, setReasonsCache]);

  const reasons = classificationId ? reasonsCache[classificationId] ?? [] : [];

  return [reasons, isLoading];
};

export default useFetchReasonsByClassification;
